import React from 'react';
import { Box, ButtonBase, Paper, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { ColorPalette } from '../../../components/config';
import { GetInterventionLadderResponse, InterventionLadderPeriod, InterventionLadderStepModel } from '../../../api-service/clients';
import { CircularProgressCentered } from '../../../components/circular-progress-centered';
import { formatNumber } from '../../../utilities';
import { stepBackgrounds, stepColor, stepIntensity } from './intervention-ladder-colors';
import { formatActivitiesWithoutPrice, formatMillions, InterventionCostLegend, InterventionCostSplit } from './intervention-cost';
import { formatPeriod, InterventionLadderCaption } from './intervention-ladder-caption';
import { InterventionLadderStaircase } from './intervention-ladder-staircase';
import { InterventionLadderStepSuppliers } from './intervention-suppliers';

const withSign = (sign: '+' | '−', value: number) => (value === 0 ? '0' : sign + formatNumber(value));

export type InterventionLadderDetails = 'moves' | 'costs' | 'suppliers';

export interface InterventionLadderComponentProps {
  period: InterventionLadderPeriod;
  currentYear: number;
  ladder: GetInterventionLadderResponse | undefined;
  hasFailed: boolean;
  selectedStep: number | undefined;
  details: InterventionLadderDetails;
  onSelectPeriod: (period: InterventionLadderPeriod) => void;
  onSelectStep: (step: InterventionLadderStepModel, intensity: number, details: InterventionLadderDetails) => void;
}

export function InterventionLadderComponent({ period, currentYear, ladder, hasFailed, selectedStep, details, onSelectPeriod, onSelectStep }: InterventionLadderComponentProps) {
  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h2" sx={{ fontSize: '1rem', mb: 2 }}>
        Indsatstrappen
      </Typography>
      <InterventionLadderCaption period={period} currentYear={currentYear} onSelectPeriod={onSelectPeriod} />
      <InterventionLadderSteps
        periodLabel={formatPeriod(period, currentYear)}
        ladder={ladder}
        hasFailed={hasFailed}
        selectedStep={selectedStep}
        details={details}
        onSelectStep={onSelectStep}
      />
    </Paper>
  );
}

interface InterventionLadderStepsProps {
  periodLabel: string;
  ladder: GetInterventionLadderResponse | undefined;
  hasFailed: boolean;
  selectedStep: number | undefined;
  details: InterventionLadderDetails;
  onSelectStep: (step: InterventionLadderStepModel, intensity: number, details: InterventionLadderDetails) => void;
}

function InterventionLadderSteps({ periodLabel, ladder, hasFailed, selectedStep, details, onSelectStep }: InterventionLadderStepsProps) {
  if (hasFailed) {
    return <Typography>Indsatstrappen kunne ikke hentes.</Typography>;
  }
  if (ladder === undefined) {
    return <CircularProgressCentered />;
  }
  const { steps, total } = ladder;
  if (steps.length === 0) {
    return <Typography>Indsatstrappen er ikke sat op.</Typography>;
  }
  return (
    <>
      <InterventionLadderStaircase steps={steps} />
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`, gap: 2 }}>
        {steps.map((step, index) => (
          <InterventionLadderStep
            key={step.step}
            step={step}
            intensity={stepIntensity(index, steps.length)}
            isSelected={step.step === selectedStep && details === 'moves'}
            onSelect={() => onSelectStep(step, stepIntensity(index, steps.length), 'moves')}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mt: 2.5, mb: 1 }}>
        <Typography variant="h3" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
          Udgifter
        </Typography>
        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>mio. kr. · {periodLabel.toLowerCase()}</Box>
        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>Klik for detaljer ▾</Box>
        <Box sx={{ ml: 'auto' }}>
          <InterventionCostLegend />
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`, gap: 2 }}>
        {steps.map((step, index) => (
          <InterventionLadderStepCost
            key={step.step}
            step={step}
            isSelected={step.step === selectedStep && details === 'costs'}
            onSelect={() => onSelectStep(step, stepIntensity(index, steps.length), 'costs')}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mt: 2.5, mb: 1 }}>
        <Typography variant="h3" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
          Eksterne leverandører
        </Typography>
        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>antal · {periodLabel.toLowerCase()}</Box>
        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>Klik for detaljer ▾</Box>
        <Box sx={{ ml: 'auto' }}>
          <InterventionCostLegend />
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`, gap: 2 }}>
        {steps.map((step, index) => (
          <InterventionLadderStepSuppliers
            key={step.step}
            step={step}
            isSelected={step.step === selectedStep && details === 'suppliers'}
            onSelect={() => onSelectStep(step, stepIntensity(index, steps.length), 'suppliers')}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mt: 2, pt: 1.6, borderTop: 1, borderColor: 'divider' }}>
        <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.84rem', color: 'text.secondary', '& strong': { color: 'text.primary', fontWeight: 500 } }}>
          <span>
            I alt <strong>{formatNumber(total.citizens)}</strong> borgere · <strong>{formatNumber(total.personYears, 1)}</strong> årspersoner
          </span>
          <Tooltip title="En borger tæller kun én gang, også når borgeren står på flere trin.">
            <FontAwesomeIcon icon={faInfoCircle} color={ColorPalette.InfoIcon} tabIndex={0} />
          </Tooltip>
        </Typography>
        <Box sx={{ width: 220, flex: 'none', fontSize: '0.84rem', color: 'text.secondary', '& strong': { color: 'text.primary', fontWeight: 500 } }}>
          <span>
            Udgift i alt <strong>{formatMillions(total.cost.total)}</strong> mio. kr.
          </span>
          <InterventionCostSplit cost={total.cost} />
          {total.cost.activitiesWithoutPrice > 0 && <Box sx={{ fontSize: '0.6875rem', mt: 0.25 }}>{formatActivitiesWithoutPrice(total.cost.activitiesWithoutPrice)}</Box>}
        </Box>
      </Box>
    </>
  );
}

interface InterventionLadderStepProps {
  step: InterventionLadderStepModel;
  intensity: number;
  isSelected: boolean;
  onSelect: () => void;
}

function InterventionLadderStep({ step, intensity, isSelected, onSelect }: InterventionLadderStepProps) {
  return (
    <ButtonBase
      component="div"
      onClick={onSelect}
      aria-pressed={isSelected}
      sx={{
        display: 'block',
        textAlign: 'left',
        font: 'inherit',
        borderRadius: '10px',
        px: 1.75,
        py: 1.5,
        boxShadow: '0 2px 6px rgba(24, 76, 97, 0.12)',
        outline: isSelected ? '3px solid #1f4d61' : 'none',
        outlineOffset: 2,
        bgcolor: stepColor(stepBackgrounds, intensity),
        color: intensity >= 0.5 ? '#ffffff' : '#16323d',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          <Typography variant="overline" sx={{ display: 'block', lineHeight: 1.4, opacity: 0.7 }}>
            Trin {step.step}
          </Typography>
          <Typography variant="subtitle2" component="div" sx={{ fontSize: '1rem', fontWeight: 500, lineHeight: 1.2 }}>
            {step.title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, flex: 'none', textAlign: 'right', lineHeight: 1 }}>
          <Box sx={{ display: 'flex', gap: 1.1 }}>
            <StepNumber value={withSign('+', step.arrivals)} label="tilgang" fontSize="0.8125rem" />
            <StepNumber value={withSign('−', step.departures)} label="afgang" fontSize="0.8125rem" />
          </Box>
          <Box>
            <StepNumber value={formatNumber(step.citizens)} label="borgere" fontSize="1.25rem" />
            <Box sx={{ mt: 0.75 }}>
              <StepNumber value={formatNumber(step.personYears, 1)} label="årspersoner" fontSize="0.8125rem" />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 0.75, p: 0, display: 'flex', flexDirection: 'column', gap: 0.25, fontSize: '0.6875rem' }}>
        {step.paragraphs.map(paragraph => (
          <Box component="li" key={paragraph.paragraph + paragraph.name} sx={{ display: 'flex', gap: 0.75 }}>
            <Box component="span" sx={{ fontWeight: 700, flex: 'none' }}>
              {paragraph.paragraph}
            </Box>
            <Box component="span" sx={{ fontWeight: 500, opacity: 0.82 }}>
              {paragraph.name}
            </Box>
          </Box>
        ))}
      </Box>
    </ButtonBase>
  );
}

interface InterventionLadderStepCostProps {
  step: InterventionLadderStepModel;
  isSelected: boolean;
  onSelect: () => void;
}

function InterventionLadderStepCost({ step, isSelected, onSelect }: InterventionLadderStepCostProps) {
  return (
    <ButtonBase
      component="div"
      onClick={onSelect}
      aria-pressed={isSelected}
      aria-label={`Udgifter på trin ${step.step}: ${formatMillions(step.cost.total)} mio. kr.`}
      sx={{
        display: 'block',
        textAlign: 'left',
        font: 'inherit',
        borderRadius: '10px',
        px: 1.75,
        py: 1.5,
        bgcolor: '#f3f8fb',
        border: '1px solid rgba(24, 76, 97, 0.12)',
        color: '#16323d',
        outline: isSelected ? '3px solid #1f4d61' : 'none',
        outlineOffset: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
        <Box sx={{ fontSize: '1.25rem', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{formatMillions(step.cost.total)}</Box>
        <Box sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>mio. kr.</Box>
      </Box>
      <InterventionCostSplit cost={step.cost} />
      {step.cost.activitiesWithoutPrice > 0 && (
        <Box sx={{ fontSize: '0.6875rem', mt: 0.25, color: 'text.secondary' }}>{formatActivitiesWithoutPrice(step.cost.activitiesWithoutPrice)}</Box>
      )}
    </ButtonBase>
  );
}

interface StepNumberProps {
  value: string;
  label: string;
  fontSize: string;
}

function StepNumber({ value, label, fontSize }: StepNumberProps) {
  return (
    <Box>
      <Box sx={{ fontSize, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
      <Box sx={{ fontSize: '0.6rem', opacity: 0.66, mt: 0.25 }}>{label}</Box>
    </Box>
  );
}
